/**
 * Main Location Financials Screen
 * Integrates all components and manages state
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import {
	FinancialsHeader,
	FinancialsGrid,
	LineItemLibraryModal,
	ImportModal,
	ExportModal,
	AddQuarterModal,
	DeleteQuarterModal,
	EmptyState,
	IncomeStatementChart,
} from '../components'
import { Alert, AlertDescription } from 'components/ui/alert'
import { Spinner } from 'components/ui/spinner'
import {
	LINE_ITEM_LIBRARY,
	defaultLabelFor,
	defaultLineItemsFor,
	detectLayout,
	type StatementLayout,
} from '../constants/lineItems'
import { generateQuarterLabels } from '../utils/formatting'
import {
	fetchIncomeStatement,
	createIncomeStatement,
	updateIncomeStatement,
	updateCell,
	addLineItem as addLineItemAction,
	removeLineItem as removeLineItemAction,
	reorderLineItems as reorderLineItemsAction,
	addQuarter as addQuarterAction,
	deleteQuarter as deleteQuarterAction,
	selectIncomeStatement,
	selectIsLoading,
	selectError,
	selectLastSaved,
	optimisticCellUpdate,
} from '../../../slices/incomeStatementSlice'
import type { LineItem } from '../types/financials.types'

export const LocationFinancials: React.FC = () => {
	const dispatch = useDispatch()
	const params = useParams<{ id: string }>()
	const { id: locationId } = params

	// Redux state
	const incomeStatement = useSelector((state: any) =>
		locationId ? selectIncomeStatement(state, locationId) : null,
	)
	const isLoading = useSelector(selectIsLoading)
	const error = useSelector(selectError)
	const lastSaved = useSelector((state: any) =>
		locationId ? selectLastSaved(state, locationId) : null,
	)

	// Local state
	const [selectedQuarterId, setSelectedQuarterId] = useState<string>('')
	const [fiscalYearStart, setFiscalYearStart] = useState(1) // January
	const [columnCount, setColumnCount] = useState(5) // Default to 5 columns
	const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false)
	const [isImportModalOpen, setIsImportModalOpen] = useState(false)
	const [isExportModalOpen, setIsExportModalOpen] = useState(false)
	const [isAddQuarterModalOpen, setIsAddQuarterModalOpen] = useState(false)
	const [isAddingQuarter, setIsAddingQuarter] = useState(false)
	const [isDeleteQuarterModalOpen, setIsDeleteQuarterModalOpen] = useState(false)
	const [quarterToDelete, setQuarterToDelete] = useState<{ id: string; label: string } | null>(null)
	const [isDeletingQuarter, setIsDeletingQuarter] = useState(false)
	const [successMessage, setSuccessMessage] = useState<string | null>(null)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [viewMode, setViewMode] = useState<'grid' | 'chart'>('grid') // Default to grid view

	// Calculated rows (Gross Profit, Earnings) are computed server-side by
	// backend/services/incomeStatementCalculator.js and arrive with their values
	// already set. There is deliberately no client-side calculation here.

	// Fetch income statement on mount
	useEffect(() => {
		if (locationId) {
			dispatch(fetchIncomeStatement({ locationId }) as any)
		}
	}, [dispatch, locationId])

	// Set initial selected quarter to the most recent (highest order)
	useEffect(() => {
		if (incomeStatement && incomeStatement.quarters.length > 0 && !selectedQuarterId) {
			// Find the most recent quarter (highest order number)
			const mostRecentQuarter = [...incomeStatement.quarters].sort((a, b) => b.order - a.order)[0]
			setSelectedQuarterId(mostRecentQuarter.id)
		}
	}, [incomeStatement, selectedQuarterId])

	// Filter quarters to show only the selected number of columns
	// Starting from the selected quarter and showing subsequent quarters to the right
	const visibleQuarters = React.useMemo(() => {
		if (!incomeStatement?.quarters) return []

		const allQuarters = [...incomeStatement.quarters].sort((a, b) => b.order - a.order) // Most recent first
		const maxColumns = Math.min(columnCount, 8) // Never show more than 8

		// If a quarter is selected, start from that quarter
		if (selectedQuarterId) {
			const selectedIndex = allQuarters.findIndex(q => q.id === selectedQuarterId)
			if (selectedIndex !== -1) {
				// Get quarters from selected onwards (descending order already, so take from that index)
				return allQuarters.slice(selectedIndex, selectedIndex + maxColumns)
			}
		}

		// Default: show most recent quarters
		return allQuarters.slice(0, maxColumns)
	}, [incomeStatement?.quarters, columnCount, selectedQuarterId])

	// Create a filtered income statement for display
	const filteredIncomeStatement = React.useMemo(() => {
		if (!incomeStatement) return null
		return {
			...incomeStatement,
			quarters: visibleQuarters,
			lineItems: incomeStatement.lineItems.map(lineItem => ({
				...lineItem,
				values: lineItem.values.filter(v => visibleQuarters.some(q => q.id === v.quarter)),
			})),
		}
	}, [incomeStatement, visibleQuarters])

	// Handlers
	const handleCellChange = useCallback(
		async (lineItemId: string, quarterId: string, value: number) => {
			if (!locationId) return

			// Optimistic update for immediate UI feedback
			dispatch(
				optimisticCellUpdate({
					locationId,
					lineItemId,
					quarterId,
					value,
				}),
			)

			// Save to backend
			try {
				await dispatch(
					updateCell({
						locationId,
						lineItemId,
						quarterId,
						value,
					}) as any,
				)
			} catch (error) {
				console.error('Failed to update cell:', error)
				// TODO: Revert optimistic update on error
			}
		},
		[dispatch, locationId],
	)

	const handleLineItemReorder = useCallback(
		async (lineItemIds: string[]) => {
			if (!locationId) return

			try {
				await dispatch(
					reorderLineItemsAction({
						locationId,
						lineItemIds,
					}) as any,
				)
			} catch (error) {
				console.error('Failed to reorder line items:', error)
			}
		},
		[dispatch, locationId],
	)

	const handleLineItemDelete = useCallback(
		async (lineItemId: string) => {
			if (!locationId) return
			if (!(await toast.confirm({ title: 'Delete line item', message: 'Delete this line item?' })))
				return

			try {
				await dispatch(
					removeLineItemAction({
						locationId,
						lineItemId,
					}) as any,
				)
				setSuccessMessage('Line item deleted successfully')
				setTimeout(() => setSuccessMessage(null), 3000)
			} catch (error) {
				console.error('Failed to delete line item:', error)
			}
		},
		[dispatch, locationId],
	)

	const handleAddLineItems = useCallback(
		async (lineItemIds: string[]) => {
			if (!locationId || !incomeStatement) return

			try {
				for (const lineItemId of lineItemIds) {
					// Find line item from library
					const libraryItem = LINE_ITEM_LIBRARY.flatMap(cat => cat.items).find(
						item => item.id === lineItemId,
					)

					if (libraryItem) {
						const newLineItem: Partial<LineItem> = {
							id: libraryItem.id,
							label: libraryItem.label,
							type: 'input',
							category: libraryItem.category,
							description: libraryItem.description,
							isDefault: false,
							order: incomeStatement.lineItems.length + 1,
							values: incomeStatement.quarters.map(q => ({
								quarter: q.id,
								value: 0,
							})),
						}

						await dispatch(
							addLineItemAction({
								locationId,
								lineItem: newLineItem,
							}) as any,
						)
					}
				}

				setSuccessMessage(`${lineItemIds.length} line item(s) added successfully`)
				setTimeout(() => setSuccessMessage(null), 3000)
			} catch (error) {
				console.error('Failed to add line items:', error)
			}
		},
		[dispatch, locationId, incomeStatement],
	)

	const handleCreateManually = useCallback(async (layout: StatementLayout = 'operator') => {
		console.log('handleCreateManually called', { locationId })

		if (!locationId) {
			console.error('Missing locationId', { locationId })
			setErrorMessage('Unable to create income statement: missing location information')
			return
		}

		try {
			// Generate quarters (5 quarters total, rolling into next year)
			const quarterLabels = generateQuarterLabels(5)
			const quarters = quarterLabels.map((label, i) => ({
				id: `q${i + 1}`,
				label,
				order: i + 1,
			}))

			// Create default income statement
			const newIncomeStatement = {
				location: locationId,
				lineItems: defaultLineItemsFor(layout).map(item => ({
					...item,
					values: quarters.map(q => ({
						quarter: q.id,
						value: 0,
					})),
				})),
				quarters,
				metadata: {
					currency: 'USD' as const,
					version: 1,
				},
			}

			console.log('Creating income statement:', newIncomeStatement)

			const result = await dispatch(
				createIncomeStatement({
					locationId,
					incomeStatement: newIncomeStatement,
				}) as any,
			)

			console.log('Create result:', result)

			if (result.error) {
				throw new Error(result.error.message || 'Failed to create income statement')
			}

			setSuccessMessage('Income statement created successfully')
			setTimeout(() => setSuccessMessage(null), 3000)
		} catch (error: any) {
			console.error('Failed to create income statement:', error)
			setErrorMessage(error.message || 'Failed to create income statement. Please try again.')
			setTimeout(() => setErrorMessage(null), 5000)
		}
	}, [dispatch, locationId])

	const handleImport = useCallback(
		async (fileName: string, options: any, importedData: any) => {
			console.log('=== HANDLE IMPORT CALLED ===')
			console.log('fileName:', fileName)
			console.log('options:', options)
			console.log('importedData:', importedData)
			console.log('locationId:', locationId)
			console.log('incomeStatement exists:', !!incomeStatement)

			if (!locationId) {
				console.error('Missing locationId')
				return
			}

			try {
				console.log('Starting import process...')

				// Rows added back below take the statement's own labels, so an owner's
				// statement gets "Net Operating Income" rather than "Earnings".
				const importLayout = detectLayout([
					...(incomeStatement?.lineItems ?? []),
					...importedData.lineItems,
				])

				// If we have an existing income statement, update it with merged data
				if (incomeStatement && options.mode === 'merge') {
					console.log('=== MERGE MODE ===')
					console.log(
						'Existing quarters:',
						incomeStatement.quarters.map(q => ({ id: q.id, label: q.label })),
					)
					console.log(
						'Imported quarters:',
						importedData.quarters.map((q: any) => ({ id: q.id, label: q.label })),
					)
					console.log(
						'Imported line items BEFORE merge:',
						importedData.lineItems.map((li: any) => ({
							id: li.id,
							type: li.type,
							formula: li.formula,
							valueCount: li.values?.length,
							sampleValue: li.values?.[0],
						})),
					)

					// Create mapping from old quarter IDs to new unique IDs FIRST
					const quarterIdMap = new Map<string, string>()
					const existingLabels = new Set(incomeStatement.quarters.map(q => q.label))
					const existingIds = new Set(incomeStatement.quarters.map(q => q.id))

					// Process imported quarters and generate unique IDs for conflicts
					importedData.quarters.forEach((importedQ: any) => {
						if (!existingLabels.has(importedQ.label)) {
							// This is a new quarter - check if ID conflicts
							let newId = importedQ.id
							if (existingIds.has(newId)) {
								// Generate unique ID
								newId = `q${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
								console.log(`Quarter ID conflict: ${importedQ.id} (${importedQ.label}) → ${newId}`)
							}
							quarterIdMap.set(importedQ.id, newId)
							existingIds.add(newId)
						}
					})

					console.log('Quarter ID mapping:', Object.fromEntries(quarterIdMap))

					// Update imported data with new quarter IDs BEFORE merging
					const updatedImportedQuarters = importedData.quarters.map((q: any) => {
						const newId = quarterIdMap.get(q.id)
						return newId ? { ...q, id: newId } : q
					})

					const updatedImportedLineItems = importedData.lineItems.map((item: any) => ({
						...item,
						values: item.values.map((v: any) => ({
							...v,
							quarter: quarterIdMap.get(v.quarter) || v.quarter,
						})),
					}))

					console.log(
						'Updated imported quarters:',
						updatedImportedQuarters.map((q: any) => ({ id: q.id, label: q.label })),
					)

					// Now merge with unique IDs
					const mergedQuarters = [...incomeStatement.quarters]
					updatedImportedQuarters.forEach((importedQ: any) => {
						if (!existingLabels.has(importedQ.label)) {
							mergedQuarters.push(importedQ)
						}
					})

					// Sort and re-number the order (create new objects to avoid mutating frozen Redux state)
					const sortedQuarters = mergedQuarters
						.sort((a, b) => {
							// Sort by year and quarter number
							const [aQ, aYear] = a.label.split(' ')
							const [bQ, bYear] = b.label.split(' ')
							if (aYear !== bYear) return parseInt(aYear) - parseInt(bYear)
							return parseInt(aQ.replace('Q', '')) - parseInt(bQ.replace('Q', ''))
						})
						.map((q, i) => ({
							...q,
							order: i + 1,
						}))

					// Get IDs of newly added quarters (already have unique IDs)
					const newQuarterIds = updatedImportedQuarters
						.filter((q: any) => !existingLabels.has(q.label))
						.map((q: any) => q.id)

					console.log('New quarter IDs to add to existing line items:', newQuarterIds)

					// Merge the imported line items with existing ones
					// For existing line items, update values from import AND add values for new quarters
					const mergedLineItems = incomeStatement.lineItems.map(existingItem => {
						// Find matching imported item
						const importedItem = updatedImportedLineItems.find(
							(imp: any) => imp.id === existingItem.id,
						)

						if (importedItem) {
							// Update existing item with imported values for matching quarters
							console.log(`Updating ${existingItem.id} with imported values`)
							const updatedValues = existingItem.values.map(existingVal => {
								// Check if imported item has value for this quarter
								const importedVal = importedItem.values?.find(
									(v: any) => v.quarter === existingVal.quarter,
								)
								if (importedVal) {
									// Use imported value
									return { ...existingVal, value: importedVal.value }
								}
								// Keep existing value
								return existingVal
							})

							// Add values for new quarters (initialized to 0)
							const newValues = newQuarterIds.map((qId: string) => ({
								quarter: qId,
								value: importedItem.values?.find((v: any) => v.quarter === qId)?.value || 0,
							}))

							return {
								...existingItem,
								type: importedItem.type ?? existingItem.type,
								formula: importedItem.formula ?? existingItem.formula,
								values: [...updatedValues, ...newValues],
							}
						} else {
							// No matching import, just add new quarters with 0
							const newValues = newQuarterIds.map((qId: string) => ({
								quarter: qId,
								value: 0,
							}))
							return {
								...existingItem,
								values: [...existingItem.values, ...newValues],
							}
						}
					})

					// Add completely new line items from import (already have updated quarter IDs)
					const newLineItems = updatedImportedLineItems.filter(
						(newItem: any) =>
							!incomeStatement.lineItems.some((existing: any) => existing.id === newItem.id),
					)

					// Combine all line items
					let allLineItems = [...mergedLineItems, ...newLineItems]

					// BUSINESS RULE #2: Auto-add calculated fields if missing
					const hasGrossProfit = allLineItems.some(item => item.id === 'gross_profit')
					const hasEarnings = allLineItems.some(item => item.id === 'earnings')

					console.log('Checking for calculated fields:', { hasGrossProfit, hasEarnings })

					if (!hasGrossProfit) {
						console.log('Adding missing Gross Profit as calculated field')
						allLineItems.push({
							id: 'gross_profit',
							label: defaultLabelFor('gross_profit', importLayout),
							type: 'calculated',
							formula: 'sales - cogs',
							category: 'revenue',
							isDefault: true,
							order: 3,
							values: sortedQuarters.map(q => ({
								quarter: q.id,
								value: 0, // Backend will calculate
							})),
						})
					}

					if (!hasEarnings) {
						console.log('Adding missing Earnings as calculated field')
						allLineItems.push({
							id: 'earnings',
							label: defaultLabelFor('earnings', importLayout),
							type: 'calculated',
							formula: 'gross_profit - expenses',
							category: 'earnings',
							isDefault: true,
							order: 5,
							values: sortedQuarters.map(q => ({
								quarter: q.id,
								value: 0, // Backend will calculate
							})),
						})
					}

					// Sort line items in the correct order: Sales, COGS, Gross Profit, Expenses, Earnings
					const orderPriority: Record<string, number> = {
						sales: 1,
						cogs: 2,
						gross_profit: 3,
						expenses: 4,
						earnings: 5,
					}

					allLineItems.sort((a, b) => {
						const aOrder = orderPriority[a.id || ''] || 999
						const bOrder = orderPriority[b.id || ''] || 999
						return aOrder - bOrder
					})

					// Update order property to match sorted position
					allLineItems = allLineItems.map((item, index) => ({
						...item,
						order: index + 1,
					}))

					const updatedIncomeStatement = {
						...incomeStatement,
						lineItems: allLineItems,
						quarters: sortedQuarters,
					}

					// Update through Redux
					await dispatch(
						updateIncomeStatement({
							locationId,
							incomeStatement: updatedIncomeStatement,
						}) as any,
					)
				} else {
					console.log('=== REPLACE MODE / NEW INCOME STATEMENT ===')
					console.log(
						'Imported line items BEFORE processing:',
						importedData.lineItems.map((li: any) => ({
							id: li.id,
							type: li.type,
							formula: li.formula,
							valueCount: li.values?.length,
							sampleValue: li.values?.[0],
						})),
					)

					// Replace mode or no existing income statement - create new
					let lineItems = [...importedData.lineItems]

					// BUSINESS RULE #2: Auto-add calculated fields if missing
					const hasGrossProfit = lineItems.some((item: any) => item.id === 'gross_profit')
					const hasEarnings = lineItems.some((item: any) => item.id === 'earnings')

					console.log('Replace mode - Checking for calculated fields:', {
						hasGrossProfit,
						hasEarnings,
					})
					console.log(
						'Line items in file:',
						lineItems.map((li: any) => ({ id: li.id, type: li.type, formula: li.formula })),
					)

					if (!hasGrossProfit) {
						console.log('Adding missing Gross Profit as calculated field')
						lineItems.push({
							id: 'gross_profit',
							label: defaultLabelFor('gross_profit', importLayout),
							type: 'calculated',
							formula: 'sales - cogs',
							category: 'revenue',
							isDefault: true,
							order: 3,
							values: importedData.quarters.map((q: any) => ({
								quarter: q.id,
								value: 0, // Backend will calculate
							})),
						})
					}

					if (!hasEarnings) {
						console.log('Adding missing Earnings as calculated field')
						lineItems.push({
							id: 'earnings',
							label: defaultLabelFor('earnings', importLayout),
							type: 'calculated',
							formula: 'gross_profit - expenses',
							category: 'earnings',
							isDefault: true,
							order: 5,
							values: importedData.quarters.map((q: any) => ({
								quarter: q.id,
								value: 0, // Backend will calculate
							})),
						})
					}

					// Sort line items in the correct order: Sales, COGS, Gross Profit, Expenses, Earnings
					const orderPriority: Record<string, number> = {
						sales: 1,
						cogs: 2,
						gross_profit: 3,
						expenses: 4,
						earnings: 5,
					}

					lineItems.sort((a: any, b: any) => {
						const aOrder = orderPriority[a.id || ''] || 999
						const bOrder = orderPriority[b.id || ''] || 999
						return aOrder - bOrder
					})

					// Update order property to match sorted position
					lineItems = lineItems.map((item: any, index: number) => ({
						...item,
						order: index + 1,
					}))

					const newIncomeStatement = {
						location: locationId,
						lineItems: lineItems,
						quarters: importedData.quarters,
						metadata: {
							currency: 'USD' as const,
							version: 1,
						},
					}

					await dispatch(
						createIncomeStatement({
							locationId,
							incomeStatement: newIncomeStatement,
						}) as any,
					)
				}

				// Refetch to ensure UI is up to date
				const refetchResult = await dispatch(fetchIncomeStatement({ locationId }) as any)

				// If quarters were added, reset the selected quarter to show the new data
				if (
					incomeStatement &&
					refetchResult.payload?.quarters?.length > incomeStatement.quarters.length
				) {
					// Find the first newly added quarter
					const newQuarters = refetchResult.payload.quarters.filter(
						(q: any) =>
							!incomeStatement.quarters.some((existing: any) => existing.label === q.label),
					)
					if (newQuarters.length > 0) {
						// Sort by order and select the first one
						const firstNewQuarter = newQuarters.sort((a: any, b: any) => a.order - b.order)[0]
						setSelectedQuarterId(firstNewQuarter.id)
					}
				} else if (!incomeStatement && refetchResult.payload?.quarters?.length > 0) {
					// If this was a new income statement (replace mode), select the first quarter
					const firstQuarter = [...refetchResult.payload.quarters].sort(
						(a: any, b: any) => a.order - b.order,
					)[0]
					setSelectedQuarterId(firstQuarter.id)
				}

				setSuccessMessage(`Successfully imported ${fileName}`)
				setTimeout(() => setSuccessMessage(null), 3000)
			} catch (error: any) {
				console.error('Failed to import:', error)
				setErrorMessage(error.message || 'Failed to import file. Please try again.')
				setTimeout(() => setErrorMessage(null), 5000)
			}
		},
		[dispatch, locationId, incomeStatement],
	)

	const handleExport = async (options: any) => {
		setSuccessMessage(`File exported successfully`)
		setTimeout(() => setSuccessMessage(null), 3000)
	}

	const handleAddQuarter = useCallback(
		async (quarterNum: number, year: number) => {
			if (!locationId || !incomeStatement) return

			setIsAddingQuarter(true)
			try {
				const quarterLabel = `Q${quarterNum} ${year}`

				// Find the highest order number
				const maxOrder = Math.max(...incomeStatement.quarters.map(q => q.order))
				const newOrder = maxOrder + 1

				// Create new quarter
				const newQuarter = {
					id: `q${Date.now()}`, // Use timestamp for unique ID
					label: quarterLabel,
					order: newOrder,
				}

				console.log('Adding quarter:', newQuarter)

				// Dispatch Redux action to add quarter
				const result = await dispatch(
					addQuarterAction({
						locationId,
						quarter: newQuarter,
					}) as any,
				)

				console.log('Add quarter result:', result)

				// Check if action was successful
				if (result.error) {
					throw new Error(result.error.message || 'Failed to add quarter')
				}

				// Ensure we have the updated data by refetching
				console.log('Refetching income statement...')
				const refetchResult = await dispatch(fetchIncomeStatement({ locationId }) as any)
				console.log('Refetch result:', refetchResult)

				setSuccessMessage(`${quarterLabel} added successfully`)
				setTimeout(() => setSuccessMessage(null), 3000)
			} catch (error: any) {
				console.error('Failed to add quarter:', error)
				setErrorMessage(error.message || 'Failed to add quarter. Please try again.')
				setTimeout(() => setErrorMessage(null), 5000)
			} finally {
				setIsAddingQuarter(false)
			}
		},
		[dispatch, locationId, incomeStatement],
	)

	const handleQuarterDeleteClick = useCallback(
		(quarterId: string) => {
			if (!incomeStatement) return
			const quarter = incomeStatement.quarters.find(q => q.id === quarterId)
			if (quarter) {
				setQuarterToDelete({ id: quarter.id, label: quarter.label })
				setIsDeleteQuarterModalOpen(true)
			}
		},
		[incomeStatement],
	)

	const handleConfirmDeleteQuarter = useCallback(async () => {
		if (!locationId || !quarterToDelete) return

		setIsDeletingQuarter(true)
		try {
			console.log('Deleting quarter:', quarterToDelete)

			const result = await dispatch(
				deleteQuarterAction({
					locationId,
					quarterId: quarterToDelete.id,
				}) as any,
			)

			console.log('Delete quarter result:', result)

			if (result.error) {
				throw new Error(result.error.message || 'Failed to delete quarter')
			}

			// Refetch to ensure UI updates
			console.log('Refetching income statement...')
			const refetchResult = await dispatch(fetchIncomeStatement({ locationId }) as any)
			console.log('Refetch result:', refetchResult)

			setSuccessMessage(`${quarterToDelete.label} deleted successfully`)
			setTimeout(() => setSuccessMessage(null), 3000)

			// Close modal and clear state
			setIsDeleteQuarterModalOpen(false)
			setQuarterToDelete(null)
		} catch (error: any) {
			console.error('Failed to delete quarter:', error)
			setErrorMessage(error.message || 'Failed to delete quarter. Please try again.')
			setTimeout(() => setErrorMessage(null), 5000)
		} finally {
			setIsDeletingQuarter(false)
		}
	}, [dispatch, locationId, quarterToDelete])

	// Loading state
	if (isLoading && !incomeStatement) {
		return <Spinner containerHeight="50vh" />
	}

	// Empty state
	if (!incomeStatement) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
				<EmptyState
					onUpload={() => setIsImportModalOpen(true)}
					onCreateManually={handleCreateManually}
				/>
				<ImportModal
					isOpen={isImportModalOpen}
					onClose={() => setIsImportModalOpen(false)}
					onImport={handleImport}
					existingQuarters={[]}
				/>
			</div>
		)
	}

	// Get location and store name from Redux (would need to implement this)
	const storeName = 'Store ' + (locationId ?? '') // Placeholder
	const storeId = locationId ?? ''

	return (
		<div className="space-y-6 pb-8">
			{/* Loading Overlays */}
			{isAddingQuarter && <Spinner containerHeight="50vh" />}
			{isDeletingQuarter && <Spinner containerHeight="50vh" />}

			{/* Success Message */}
			{successMessage && (
				<Alert variant="success">
					<AlertDescription className="flex items-center justify-between">
						<span>{successMessage}</span>
						<button onClick={() => setSuccessMessage(null)} className="ml-4 p-1 hover:bg-white/20 rounded" aria-label="Dismiss">✕</button>
					</AlertDescription>
				</Alert>
			)}

			{/* Error Message */}
			{error && (
				<Alert variant="danger">
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}
			{errorMessage && (
				<Alert variant="danger">
					<AlertDescription className="flex items-center justify-between">
						<span>{errorMessage}</span>
						<button onClick={() => setErrorMessage(null)} className="ml-4 p-1 hover:bg-white/20 rounded" aria-label="Dismiss">✕</button>
					</AlertDescription>
				</Alert>
			)}

			{/* Header */}
			<div>
				<div className="pb-2">
					<FinancialsHeader
						storeName={storeName}
						storeId={storeId}
						quarters={incomeStatement.quarters}
						selectedQuarterId={selectedQuarterId}
						onQuarterChange={setSelectedQuarterId}
						fiscalYearStart={fiscalYearStart}
						onFiscalYearStartChange={setFiscalYearStart}
						columnCount={columnCount}
						onColumnCountChange={setColumnCount}
						onAddQuarter={() => setIsAddQuarterModalOpen(true)}
						onAddLineItem={() => setIsLineItemModalOpen(true)}
						onUpload={() => setIsImportModalOpen(true)}
						onExport={() => setIsExportModalOpen(true)}
						lastSavedAt={lastSaved || undefined}
						lastSavedBy={incomeStatement.metadata.lastModifiedBy}
						viewMode={viewMode}
						onViewModeChange={setViewMode}
					/>
				</div>
			</div>

			{/* Conditionally Render Grid or Chart */}
			{viewMode === 'grid' && filteredIncomeStatement && (
				<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
					<FinancialsGrid
						incomeStatement={filteredIncomeStatement}
						onCellChange={handleCellChange}
						onLineItemReorder={handleLineItemReorder}
						onLineItemDelete={handleLineItemDelete}
						onQuarterDelete={handleQuarterDeleteClick}
						isLoading={isLoading}
					/>
				</div>
			)}

			{viewMode === 'chart' && filteredIncomeStatement && (
				<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
					<div className="mb-4">
						<h2 className="text-xl font-semibold text-gray-900">Trend Analysis</h2>
						<p className="text-sm text-gray-600 mt-1">
							Visual representation of income statement trends across quarters
						</p>
					</div>
					<IncomeStatementChart incomeStatement={filteredIncomeStatement} />
				</div>
			)}

			{/* Modals */}
			<LineItemLibraryModal
				isOpen={isLineItemModalOpen}
				onClose={() => setIsLineItemModalOpen(false)}
				availableLineItems={LINE_ITEM_LIBRARY}
				addedLineItemIds={incomeStatement.lineItems.map(li => li.id)}
				onAddLineItems={handleAddLineItems}
			/>

			<ImportModal
				isOpen={isImportModalOpen}
				onClose={() => setIsImportModalOpen(false)}
				onImport={handleImport}
				existingQuarters={incomeStatement?.quarters || []}
			/>

			{incomeStatement && (
				<ExportModal
					isOpen={isExportModalOpen}
					onClose={() => setIsExportModalOpen(false)}
					incomeStatement={incomeStatement}
					onExport={handleExport}
				/>
			)}

			<AddQuarterModal
				isOpen={isAddQuarterModalOpen}
				onClose={() => setIsAddQuarterModalOpen(false)}
				onAddQuarter={handleAddQuarter}
				existingQuarters={incomeStatement?.quarters.map(q => ({ label: q.label, id: q.id }))}
			/>

			{quarterToDelete && (
				<DeleteQuarterModal
					isOpen={isDeleteQuarterModalOpen}
					onClose={() => {
						setIsDeleteQuarterModalOpen(false)
						setQuarterToDelete(null)
					}}
					onConfirmDelete={handleConfirmDeleteQuarter}
					quarterLabel={quarterToDelete.label}
				/>
			)}
		</div>
	)
}

export default LocationFinancials
